import { useEffect, useRef } from "react";
import { useLocation, useNavigationType } from "react-router-dom";

/**
 * Ripristino della posizione di scorrimento PRINCIPALE della pagina.
 * - Navigazione in avanti (PUSH/REPLACE): la nuova pagina parte dall'alto.
 * - Tasto "indietro"/"avanti" del browser (POP): torna ESATTAMENTE alla posizione
 *   in cui eri, riprovando finché il contenuto (caricato in modo asincrono) è
 *   abbastanza alto da raggiungerla.
 *
 * Lavora con `BrowserRouter` (React Router 7 in modalità dichiarativa) senza
 * toccare le pagine esistenti.
 */
export default function ScrollManager() {
    const location = useLocation();
    const navType = useNavigationType(); // "POP" | "PUSH" | "REPLACE"
    const positions = useRef(new Map());
    const currentKey = useRef(location.key);
    const restoring = useRef(false);

    // Prende il controllo manuale dello scroll dal browser.
    useEffect(() => {
        if (!("scrollRestoration" in window.history)) return;
        const prev = window.history.scrollRestoration;
        window.history.scrollRestoration = "manual";
        return () => {
            window.history.scrollRestoration = prev || "auto";
        };
    }, []);

    // Salva di continuo la posizione della pagina corrente (per la sua voce di
    // cronologia). NON salva durante un ripristino in corso, per non sovrascrivere
    // la posizione target con valori intermedi (pagina ancora corta → clamp).
    useEffect(() => {
        const onScroll = () => {
            if (restoring.current) return;
            positions.current.set(currentKey.current, window.scrollY);
        };
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        currentKey.current = location.key;

        if (navType === "POP") {
            const target = positions.current.get(location.key) ?? 0;
            if (target <= 0) {
                window.scrollTo(0, 0);
                return;
            }
            // Ripristino robusto: ri-applica la posizione ad OGNI frame per ~3s.
            // Serve per le pagine che caricano i dati in modo asincrono (es. scheda
            // squadra: prima un placeholder corto, poi la rosa completa). Così si
            // atterra ESATTAMENTE nel punto in cui eri, vincendo su qualsiasi scroll
            // intermedio (clamp sul placeholder corto, anchoring, ecc.). Il loop si
            // ferma appena la posizione è raggiunta.
            restoring.current = true;
            const start = performance.now();
            let raf = 0;
            let reachedSince = 0;
            const stop = () => {
                restoring.current = false;
                if (raf) cancelAnimationFrame(raf);
            };
            const loop = () => {
                window.scrollTo(0, target);
                const ok = Math.abs(window.scrollY - target) <= 2;
                const now = performance.now();
                // Considera finito solo dopo ~250ms di posizione stabile (il contenuto
                // asincrono potrebbe ancora crescere e richiedere un nuovo aggancio).
                if (ok) {
                    if (!reachedSince) reachedSince = now;
                } else {
                    reachedSince = 0;
                }
                if ((reachedSince && now - reachedSince > 250) || now - start > 3000) {
                    stop();
                    return;
                }
                raf = requestAnimationFrame(loop);
            };
            raf = requestAnimationFrame(loop);
            return stop;
        }
        // PUSH / REPLACE → nuova pagina dall'alto.
        window.scrollTo(0, 0);
    }, [location.key, navType]);

    return null;
}
